import React from 'react';
import './About.css';
import { useMetaTags } from '../hooks/useMetaTags';

function About() {
  useMetaTags({
    title: 'About DXB Dates - Discover Dubai\'s Best Dining Deals',
    description: 'Learn about DXB Dates, your guide to the best 2 for 1 restaurant deals in Dubai. Explore Dubai\'s culinary scene without breaking the bank.',
    ogTitle: 'About DXB Dates',
    ogImage: 'https://dxbdates.com/og-image.jpg'
  });

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-content">
          <h1>About Us</h1>
          <p>Discover Dubai's culinary playground without breaking the bank</p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="about-content-section">
        <div className="about-container">
          <div className="about-content">
            <div className="about-text">
              <div className="about-intro">
                <p className="lead-text">
                  Dubai is more than just skyscrapers and sunshine — it's a culinary playground waiting to be explored. At DXB Dates, we believe that discovering amazing food shouldn't come with a hefty price tag.
                </p>
              </div>

              <div className="about-story">
                <h2>Our Story</h2>
                <p>
                  Like many food lovers in the city, we found ourselves overwhelmed by endless apps and confusing deals. We wanted to enjoy Dubai's vibrant dining scene — from hidden gems in Karama to rooftop lounges downtown — without constantly checking six different platforms. So we decided to do something about it.
                </p>
                
                <p>
                  We started DXB Dates as a simple way to keep track of the best affordable restaurants and dining deals in town. What began as a personal list grew into a community-driven platform where anyone can explore Dubai's food scene without breaking the bank.
                </p>
              </div>

              <div className="about-mission">
                <h2>Our Mission</h2>
                <p>
                  Our mission is to make it easier for everyone — locals, expats, and travelers alike — to experience the rich culinary diversity Dubai has to offer. Whether you're planning a date night, catching up with friends, or just craving something new, we've got your next meal covered.
                </p>
              </div>

              <div className="about-welcome">
                <div className="welcome-card">
                  <h3>Welcome to DXB Dates</h3>
                  <p>Where good taste meets smart spending.</p>
                  <div className="welcome-actions">
                    <a href="/view-restaurants" className="btn btn-primary">Explore Restaurants</a>
                    <a href="/contact" className="btn btn-outline">Get in Touch</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="about-features">
              <h2>What We Offer</h2>
              <div className="features-grid">
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
                      <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115l.094-.319z"/>
                    </svg>
                  </div>
                  <h3>Curated Deals</h3>
                  <p>Hand-picked restaurant deals and offers from across Dubai, updated regularly to ensure you never miss out.</p>
                </div>

                <div className="feature-card">
                  <div className="feature-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                    </svg>
                  </div>
                  <h3>Local Insights</h3>
                  <p>From hidden gems in Karama to rooftop lounges downtown, discover the best spots locals actually visit.</p>
                </div>

                <div className="feature-card">
                  <div className="feature-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M1 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                      <path d="M14 14s1 0 1-1-1-4-6-4-6 3-6 4 1 1 1 1h10zm-5.5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                    </svg>
                  </div>
                  <h3>Community Driven</h3>
                  <p>Built by food lovers, for food lovers. Join a community that shares your passion for great food at great prices.</p>
                </div>

                <div className="feature-card">
                  <div className="feature-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M2.5 3A1.5 1.5 0 0 0 1 4.5v.793c.026.009.051.02.076.032L7.674 8.51c.206.1.446.1.652 0l6.598-3.185A.755.755 0 0 1 15 5.293V4.5A1.5 1.5 0 0 0 13.5 3h-11Z"/>
                      <path d="M15 6.954 8.978 9.86a2.25 2.25 0 0 1-1.956 0L1 6.954V11.5A1.5 1.5 0 0 0 2.5 13h11a1.5 1.5 0 0 0 1.5-1.5V6.954Z"/>
                    </svg>
                  </div>
                  <h3>No Spam, Just Value</h3>
                  <p>We respect your time and inbox. Only the best deals and restaurant recommendations make it to our platform.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
